import React, { useEffect, useState } from "react";
import "./Accounting.css";
import account_dashboard from "../images/Acc-Dashboard.png";

const services = [
  {
    number: "01",
    title: "Management accounts",
    description:
      "Clear monthly reporting across individual homes or your whole group, with the numbers explained in plain English.",
  },

  {
    number: "02",
    title: "Payroll & pensions",
    description:
      "Accurate, dependable payroll for complex care teams—including hourly rates, overtime, enhancements and pension duties.",
  },
  {
    number: "03",
    title: "Bookkeeping & payments",
    description:
      "Day-to-day bookkeeping, reconciliations, supplier payments and fee tracking kept controlled and up to date.",
  },
  {
    number: "04",
    title: "Budgeting & forecasting",
    description:
      "Occupancy-led forecasts, staffing cost models and cash-flow planning that help you make confident decisions.",
  },
  {
    number: "05",
    title: "Year-end & tax",
    description:
      "Statutory accounts, corporation tax and year-end preparation coordinated without the usual last-minute scramble.",
  },
  {
    number: "06",
    title: "Growth & acquisitions",
    description:
      "Financial due diligence, funding packs and post-acquisition reporting for operators expanding their portfolio.",
  },
];

const whyItems = [
  {
    number: "01",
    title: "Sector context",
    description:
      "Our accounting support is designed specifically around care businesses.",
  },
  {
    number: "02",
    title: "Clear reporting",
    description:
      "We turn financial information into reports that are easy to understand.",
  },
  {
    number: "03",
    title: "Practical advice",
    description:
      "Get useful financial advice that supports real operational decisions.",
  },
  {
    number: "04",
    title: "Ongoing support",
    description: "We work alongside your team as your organisation grows.",
  },
];

const careBusinesses = [
  "Residential care homes",
  "Nursing homes",
  "Domiciliary care",
  "Supported living",
  "Care home groups",
  "New care providers",
];

const Accounting = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
  });

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    setStatus({
      type: "",
      message: "",
    });

    try {
      const response = await fetch("/send-consultation.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: "success",
          message:
            "Thank you! Your consultation request has been sent successfully.",
        });

        // Clear form
        setFormData({
          name: "",
          email: "",
          organisation: "",
        });
      } else {
        setStatus({
          type: "error",
          message: result.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);

      setStatus({
        type: "error",
        message: "Unable to send your request. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="accountancy-page">
      <section className="accountancy-banner">
        <div className="accountancy-container banner-grid">
          <div className="banner-content">
            <span className="eyebrow banner-eyebrow">
              ACCOUNTANCY BUILT AROUND CARE
            </span>

            <h1>Care sector finances, handled with clarity.</h1>

            <p className="banner-description">
              Specialist accounting, payroll, management accounts and
              forecasting for care homes and care groups. Get reliable
              numbers—and the insight to act on them.
            </p>

            <div className="banner-actions">
              <button className="primary-button">
                <a href="mailto:info@totalcareadvisory.com?subject=Total%20Care%20Rota%20Demo">
                  Book a free consultation
                </a>
              </button>

              <a href="#introduction" className="secondary-link">
                Explore our services
              </a>
            </div>

            <p className="banner-note">
              ✓ Built for the realities of care. From occupancy and agency spend
              to payroll and fee income.
            </p>
          </div>

          <div className="banner-dashboard">
            <img src={account_dashboard} alt="Financial Overview Dashboard" />
          </div>
        </div>
      </section>
      <section className="feature-strip">
        <div className="feature-container">
          <div className="feature-card">
            <h3>Care-specific</h3>
            <p>Specialist sector knowledge</p>
          </div>

          <div className="feature-card">
            <h3>Clear reporting</h3>
            <p>Numbers you can use</p>
          </div>

          <div className="feature-card">
            <h3>Scalable</h3>
            <p>One service for every home</p>
          </div>

          <div className="feature-card">
            <h3>Responsive</h3>
            <p>A team that knows your business</p>
          </div>
        </div>
      </section>
      <nav className="section-navigation">
        <a href="#services">Services</a>

        <a href="#why-us">Why us</a>

        <a href="#who-we-help">Who we help</a>

        <a href="#contact">Contact</a>
      </nav>

      <section className="services-introduction" id="introduction">
        <div className="accountancy-container services-introduction-grid">
          <div className="services-introduction-left">
            <span className="eyebrow">WHAT WE DO</span>

            <h2>
              Everything your care business
              <br className="desktop-only" />
              needs from its finance team.
            </h2>
          </div>

          <div className="services-introduction-right">
            <p>
              We provide everything from accounting and payroll to tax,
              forecasting and business consultancy—giving your organisation one
              trusted finance partner.
            </p>
          </div>
        </div>
      </section>

      <section className="services-section" id="services">
        <div className="accountancy-container services-grid">
          {services.map((service) => (
            <article className="service-card" key={service.number}>
              <span className="service-number">{service.number}</span>

              <h3>{service.title}</h3>

              <p>{service.description}</p>

              <a href="#contact">Learn more ↗</a>
            </article>
          ))}
        </div>
      </section>

      <section className="why-section" id="why-us">
        <div className="accountancy-container why-grid">
          <div className="why-content">
            <span className="eyebrow why-eyebrow">
              — WHY TOTAL CARE ADVISORY
            </span>

            <h2>
              Your accountant should
              <br />
              understand more than the
              <br />
              accounts.
            </h2>

            <p>
              Generic reports only tell half the story. We understand how
              occupancy, local authority fees, staffing ratios, agency use and
              payroll pressures shape performance in care.
            </p>

            <button className="light-button">
              <a href="tel:+440000000000">Meet your finance team ↗</a>
            </button>
          </div>

          <div className="why-list">
            {whyItems.map((item) => (
              <div className="why-item" key={item.number}>
                <span className="why-number">{item.number}</span>

                <div className="why-item-content">
                  <h3>{item.title}</h3>

                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="who-section" id="who-we-help">
        <div className="who-card">
          <div className="who-content">
            <span className="eyebrow">— WHO WE HELP</span>

            <h2>
              Accountancy that grows
              <br />
              with your care business.
            </h2>

            <p>
              Whether you are opening your first service or consolidating
              reporting across a growing group, we shape the support around your
              structure and ambitions.
            </p>
          </div>

          <div className="care-business-grid">
            {careBusinesses.map((business) => (
              <div className="care-business" key={business}>
                <span className="check-icon">✓</span>

                <span>{business}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section className="contact-section" id="contact">
        <div className="accountancy-container contact-grid">
          <div className="contact-content">
            <span className="eyebrow">— LET'S TALK NUMBERS</span>

            <h1 >
              Ready for a clearer
              <br />
              view of your care
              <br />
              business?
            </h1>

            <p>
              Book a free, no-obligation consultation with our team. Tell us a
              little about your organisation and we'll arrange a convenient time
              to talk.
            </p>

            <div className="contact-details">
              <div>
                <span>Email</span>
                <a href="mailto:info@totalcareadvisory.co.uk">
                  info@totalcareadvisory.co.uk
                </a>
              </div>
            </div>
          </div>

          {/* ================= CONSULTATION FORM ================= */}
          <div className="consultation-card">
            <h3>Book your free consultation</h3>

            <p className="consultation-description">
              Tell us a little about your organisation and we'll arrange a
              convenient time to talk.
            </p>

            <form onSubmit={handleSubmit}>
              {/* NAME + EMAIL */}
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name">Your name</label>

                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="email">Work email</label>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@carebusiness.co.uk"
                    required
                  />
                </div>
              </div>

              {/* ORGANISATION */}
              <div className="form-field">
                <label htmlFor="organisation">Organisation</label>

                <input
                  id="organisation"
                  type="text"
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  placeholder="Care business name"
                  required
                />
              </div>

              {/* STATUS MESSAGE */}
              {status.message && (
                <div className={`form-message ${status.type}`}>
                  {status.message}
                </div>
              )}

              {/* SUBMIT */}
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Request a consultation →"}
              </button>
            </form>

            <p className="email-note">
              Prefer email?{" "}
              <a href="mailto:info@totalcareadvisory.co.uk">
                info@totalcareadvisory.co.uk
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Accounting;
